/**
 * @file Notifications toast event bus utilities.
 * Simple pub/sub to request UI toast for a given notification row.
 */

import type { NotificationRow } from '@/types/notifications';

/** Payload for a toast event triggered by a notification arrival. */
export interface ToastNotificationEvent {
  row: NotificationRow;
}

type Listener = (event: ToastNotificationEvent) => void;

const listeners: Set<Listener> = new Set();

/**
 * Subscribe to toast events. Returns an unsubscribe function.
 * @param listener Callback invoked when a toast should be shown.
 */
export function onNotificationToast(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Emit a toast event for the provided notification row.
 * Safe to call from services and stores.
 */
export function emitNotificationToast(row: NotificationRow): void {
  const event: ToastNotificationEvent = { row };
  for (const l of Array.from(listeners)) {
    try {
      l(event);
    } catch {
      // Listener errors are isolated to avoid breaking others
    }
  }
}
